import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import Header from "@/components/Header";
import Sidebar from "@/components/Sidebar";
import { cn } from "@/lib/utils";
import { subscriptionPlans } from "@/utils/subscriptionPlans";
import { toast } from "@/components/ui/use-toast";
import { createCheckoutSession } from "@/integrations/stripe/api";
import { stripePromise } from "@/integrations/stripe/client";
import { useAuth } from "@/contexts/AuthContext";
import { SubscriptionSelection } from "@/types/subscription";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { getSubscriptionData } from "@/lib/supabase";

const Subscription = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selection, setSelection] = useState<SubscriptionSelection | null>(null);
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'annual'>('monthly');
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);

  // Read session_id from URL
  const searchParams = new URLSearchParams(location.search);
  const sessionId = searchParams.get("session_id");

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleSubscribe = async () => {
    if (!selection) {
      toast({
        title: "No Plan Selected",
        description: "Please select a subscription plan to continue.",
        variant: "destructive",
      });
      return;
    }

    if (!user) {
      toast({
        title: "Not Authenticated",
        description: "Please sign in to subscribe.",
        variant: "destructive",
      });
      navigate("/auth");
      return;
    }

    setIsLoading(true);
    try {
      console.log('Creating checkout session with:', {
        planId: selection.planId,
        interval: selection.interval,
        userId: user.id,
        email: user.email
      });

      // Create a Stripe Checkout Session with the selected plan and interval
      const sessionId = await createCheckoutSession(selection.planId, selection.interval);
      console.log('Received session ID:', sessionId);
      
      // Load Stripe.js
      const stripe = await stripePromise;
      if (!stripe) {
        console.error('Failed to load Stripe');
        throw new Error('Failed to load Stripe');
      }

      console.log('Redirecting to checkout...');
      // Redirect to Checkout
      const { error } = await stripe.redirectToCheckout({ sessionId });
      if (error) {
        console.error('Stripe redirect error:', error);
        throw error;
      }
    } catch (error: any) {
      console.error('Subscription error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to process subscription. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getPrice = (plan: typeof subscriptionPlans[0]) => {
    const price = billingInterval === 'monthly' ? plan.monthlyPrice : plan.annualPrice;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const annualSavings = (monthlyPrice: number, annualPrice: number) => {
    const monthlyCost = monthlyPrice * 12;
    const savings = monthlyCost - annualPrice;
    return Math.round((savings / monthlyCost) * 100);
  };

  useEffect(() => {
    const fetchSubscription = async () => {
      if (!user) {
        setIsFetching(false);
        return;
      }
      try {
        const sub = await getSubscriptionData(user.id);
        if (sub) {
          // Debug: log planId from Supabase and local plan config IDs
          console.log('Supabase plan_id:', sub.plan_id, 'Local plan ids:', subscriptionPlans.map(p => p.id));
          // Normalize and robustly compare planId
          const normalizedPlanId = (sub.plan_id || '').trim().toLowerCase();
          const matchedPlan = subscriptionPlans.find(p => p.id.trim().toLowerCase() === normalizedPlanId);
          setSelection(matchedPlan ? { planId: matchedPlan.id, interval: sub.interval === 'year' ? 'annual' : 'monthly' } : null);
          setBillingInterval(sub.interval === 'year' ? 'annual' : 'monthly');
        }
      } catch (e) {
        // Optionally handle error
      } finally {
        setIsFetching(false);
      }
    };
    fetchSubscription();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Show toast on successful subscription
  useEffect(() => {
    if (sessionId) {
      toast({
        title: 'Subscription successful!',
        description: 'Thank you for subscribing. Your payment has been processed.',
        duration: 5000,
        variant: 'success',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header toggleSidebar={toggleSidebar} />
      <div className="flex flex-1 overflow-hidden">
        <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
        
        <main className={cn(
          "flex-1 transition-all duration-300 overflow-y-auto",
          sidebarOpen ? "md:ml-64" : ""
        )}>
          <div className="py-8 px-6 max-w-6xl mx-auto">
            {/* Success message and Settings button layout update */}
            <div className="flex flex-col gap-2 mb-6">
              <div className="flex items-center gap-4">
                <Button
                  variant="outline"
                  className="self-start"
                  onClick={() => navigate('/settings?tab=subscription')}
                >
                  Go to Settings
                </Button>
              </div>
            </div>
            {/* Loading state while fetching subscription */}
            {isFetching ? (
              <div className="text-center py-10 text-muted-foreground">Loading your subscription...</div>
            ) : (
              <>
            <div className="text-center mb-10">
              <h1 className="text-2xl font-bold">Choose Your Plan</h1>
              <p className="text-muted-foreground mt-2 max-w-xl mx-auto">
                Select the subscription plan that best fits your competitive benchmarking needs.
                All plans include a 7-day free trial.
              </p>
              {/* Billing Interval Toggle */}
              <div className="mt-6 inline-flex items-center gap-4 bg-white p-1 rounded-lg border">
                <Label className={cn(
                  "px-3 py-1 rounded cursor-pointer",
                  billingInterval === 'monthly' ? "bg-primary text-white" : "text-muted-foreground"
                )} onClick={() => setBillingInterval('monthly')}>
                  Monthly
                </Label>
                <Switch
                  checked={billingInterval === 'annual'}
                  onCheckedChange={(checked) => setBillingInterval(checked ? 'annual' : 'monthly')}
                />
                <Label className={cn(
                  "px-3 py-1 rounded cursor-pointer",
                  billingInterval === 'annual' ? "bg-primary text-white" : "text-muted-foreground"
                )} onClick={() => setBillingInterval('annual')}>
                  Annual
                </Label>
              </div>
              {billingInterval === 'annual' && (
                <p className="text-sm text-green-600 mt-2">
                  Save up to 15% with annual billing
                </p>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {subscriptionPlans.map((plan) => (
                <Card 
                  key={plan.id} 
                  className={cn(
                    "relative",
                    plan.popular ? "border-accent" : "",
                    selection?.planId === plan.id ? "ring-2 ring-accent" : ""
                  )}
                >
                  {plan.popular && (
                    <div className="absolute top-0 right-0 transform translate-x-2 -translate-y-2">
                      <span className="bg-accent text-white text-xs font-medium rounded-full px-3 py-1">
                        Popular
                      </span>
                    </div>
                  )}
                  <CardHeader>
                    <CardTitle>{plan.name}</CardTitle>
                    <CardDescription>{plan.description}</CardDescription>
                    <div className="mt-4">
                      <span className="text-3xl font-bold">{getPrice(plan)}</span>
                      <span className="text-muted-foreground">/{billingInterval === 'monthly' ? 'month' : 'year'}</span>
                      {billingInterval === 'annual' && (
                        <div className="text-sm text-green-600 mt-1">
                          Save {annualSavings(plan.monthlyPrice, plan.annualPrice)}%
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {plan.features.map((feature, index) => (
                        <li key={index} className="flex items-start">
                          <Check className="h-5 w-5 text-green-500 shrink-0 mr-2" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-6">
                      <h4 className="font-medium mb-2">Included Modules:</h4>
                      <ul className="space-y-1">
                        {plan.modules.map((module, index) => (
                          <li key={index} className="text-sm">
                            {module}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                  <CardFooter>
                    <Button
                      className={cn("w-full", plan.popular ? "bg-accent hover:bg-accent/90" : "")}
                      onClick={() => setSelection({ planId: plan.id, interval: billingInterval })}
                      variant={selection?.planId === plan.id ? "default" : "outline"}
                    >
                      {selection?.planId === plan.id ? "Selected" : "Select Plan"}
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
            
            <div className="mt-10 text-center">
              <Button 
                size="lg" 
                onClick={handleSubscribe}
                disabled={!selection || isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  selection ? "Subscribe Now" : "Select a Plan"
                )}
              </Button>
              <p className="text-sm text-muted-foreground mt-4">
                You can cancel or change your plan at any time.
                All plans include a 7-day free trial. No credit card required for trial.
              </p>
            </div>
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Subscription;
